import {
    useCallback,
    useEffect,
} from 'react';
import {
    Controller,
    useForm,
} from 'react-hook-form';
import {
    Link,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
} from '@/components/ui/card';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from '@/components/ui/input-otp';
import { baseRequest } from '@/lib/base';

const otpSchema = z.object({
    otp: z.string().length(6, 'Please enter all 6 digits'),
});

type OTPFormData = z.infer<typeof otpSchema>

// API: verify forgot-password OTP
const verifyForgotPasswordOTP = async (email: string, verificationCode: string) => {
    const response = await baseRequest({
        url: '/system/auth/verify-forgot-password/',
        method: 'POST',
        body: JSON.stringify({ email, verification_code: verificationCode }),
        contentType: 'application/json',
    });
    return response;
};

// API: verify registration OTP
const verifyRegistrationOTP = async (email: string, verificationCode: string) => {
    const response = await baseRequest({
        url: '/system/auth/verify-email/',
        method: 'POST',
        body: JSON.stringify({ email, verification_code: verificationCode }),
        contentType: 'application/json',
    });
    return response;
};

// API: resend OTP
const resendOTP = async (email: string, flow: string) => {
    const url = flow === 'registration'
        ? '/system/auth/resend-verification-code/'
        : '/system/auth/resend-forgot-password/';
    const response = await baseRequest({
        url,
        method: 'POST',
        body: JSON.stringify({ email }),
        contentType: 'application/json',
    });
    return response;
};

export default function OTPVerification() {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { email?: string; flow?: 'registration' | 'forgot-password' } | null;
    const email = state?.email;
    const flow = state?.flow || 'forgot-password';

    useEffect(() => {
        if (!email) {
            navigate(flow === 'registration' ? '/signup' : '/forgot-password', { replace: true });
        }
    }, [email, flow, navigate]);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<OTPFormData>({
        resolver: zodResolver(otpSchema),
        defaultValues: { otp: '' },
    });

    // Verify OTP mutation
    const verifyMutation = useMutation({
        mutationFn: (otp: string) => {
            if (flow === 'registration') {
                return verifyRegistrationOTP(email!, otp);
            }
            return verifyForgotPasswordOTP(email!, otp);
        },
        onSuccess: (response) => {
            if (response.ok) {
                if (flow === 'registration') {
                    toast.success('Email verified successfully! Please login.');
                    navigate('/login', { replace: true });
                } else {
                    toast.success(response.data?.detail || 'OTP verified successfully!');
                    navigate('/new-password', { state: { email } });
                }
            } else {
                toast.error(response.data?.detail || 'Invalid OTP. Please try again.');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    // Resend OTP mutation
    const resendMutation = useMutation({
        mutationFn: () => resendOTP(email!, flow),
        onSuccess: (response) => {
            if (response.ok) {
                toast.success(response.data?.detail || 'OTP has been resent to your email');
            } else {
                toast.error(response.data?.detail || 'Failed to resend OTP');
            }
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    const onSubmit = useCallback((data: OTPFormData) => {
        verifyMutation.mutate(data.otp);
    }, [verifyMutation]);

    const handleResendOTP = useCallback(() => {
        resendMutation.mutate();
    }, [resendMutation]);

    if (!email) return null;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top nav */}
            <div className="p-6">
                <Link
                    to={flow === 'registration' ? '/signup' : '/forgot-password'}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="flex flex-col gap-6 w-full max-w-sm">
                    {/* Logo and header */}
                    <div className="flex flex-col items-center gap-3">
                        <Link to="/">
                            <img src="/chanco-vertical.png" alt="Chanco" className="h-24 w-auto" />
                        </Link>
                        <h1 className="text-2xl font-bold text-foreground">OTP Verification</h1>
                        <p className="text-sm text-muted-foreground text-center">
                            A 6 digit code has been sent to your E-mail
                        </p>
                    </div>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid gap-5">
                                    {/* OTP input */}
                                    <div className="flex flex-col items-center gap-2">
                                        <Controller
                                            name="otp"
                                            control={control}
                                            render={({ field }) => (
                                                <InputOTP
                                                    maxLength={6}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                >
                                                    <InputOTPGroup>
                                                        {[0, 1, 2, 3, 4, 5].map((index) => (
                                                            <InputOTPSlot
                                                                key={index}
                                                                index={index}
                                                                className="h-12 w-12 text-lg"
                                                            />
                                                        ))}
                                                    </InputOTPGroup>
                                                </InputOTP>
                                            )}
                                        />
                                        {errors.otp && <span className="text-sm text-destructive">{errors.otp.message}</span>}
                                    </div>

                                    {/* Continue button */}
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={verifyMutation.isPending}
                                    >
                                        {verifyMutation.isPending ? 'Verifying...' : 'Continue'}
                                    </Button>

                                    {/* Resend OTP */}
                                    <p className="text-center text-sm text-muted-foreground">
                                        Didn&apos;t receive the code?
                                        {' '}
                                        <button
                                            type="button"
                                            onClick={handleResendOTP}
                                            disabled={resendMutation.isPending}
                                            className="text-primary font-medium hover:underline disabled:opacity-50"
                                        >
                                            {resendMutation.isPending ? 'Sending...' : 'Resend OTP'}
                                        </button>
                                    </p>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
